import React, { useEffect, useState, useRef, useLayoutEffect, useCallback } from "react";
import ReactDatePicker from "react-datepicker";
import Loader from "react-spinner-loader";
import moment from "moment";
import { toast } from "react-toastify";
import "react-datepicker/dist/react-datepicker.css";
import { GridStack } from "gridstack";
import FamilyAffectedTrendView from "./FamilyAffectedTrendView";
import DoughnutChart from "./DoughnutChart";
import { getData, postDataWithBody } from "../../../Services/Services";
import {
  getStateFloodCountData,
  getBlockWiseReport,
  getAffectedBlock,
  getAffectedDistrict,
  getAffectedNagarNigam,
  getAffectedPanchayat,
  getAffectedDrilldownView,
  getCurrStateFloodCountData,
  getCurrBarChartData,
  getThemeURL,
  dashboardSummaryData,
  getServerDateAndTime
} from "../../../Services/EndPoints";
import "./MainDashboard.css";
import "./DashboardTheme.css";
import { useTranslation } from "react-i18next";
import i18n from "i18next";
import {
  Expand,
  HumanLoss,
  IncidentFacility,
  MapIcon,
} from "../../../Components/IconComponents";
import { FullScreen, useFullScreenHandle } from "react-full-screen";
import DashboardCard from "./StateDashboard/DashboardCard/DashboardCard";
import {
  currentCardConfig,
  dashboardCardConfig,
} from "./StateDashboard/StateDashboardConfig";
import LMSButton from "../Common/LMSButton/LMSButton";
import HalfDoughnutChart from "./HalfDoughnutChart";
import HorizontalBarChart from "./HorizontalBarChart";
import CurrentDashboardCard from "./CurrentDashboardCard/CurrentDashboardCard";
import TabSwitch from "./TabSwitch/TabSwitch";
import { useNavigate } from "react-router-dom";
import WeatherDataTable from "./WeatherDataTable";
import { maindashboardCardConfig } from "./MainDashboardConfig";
import MainDashboardCard from "./MainDashboardCard";
import MainDashboardWeatherDataTable from "./MainDashboardWeatherData";
import DMSDashboard from "./DMSDashboard/DMSDashboard";
import RTIncidentDashboard from "../GisManagement/RTIncidentDashboard";
import MainDashboardHalfDoughnutChart from "./MainDashboardDoughnut";
import FamilyAffectedTrendViewHumanLoss from "./HumanLossDashboard/DashboardCard/FamilyAffectedTrendViewHumanLoss";
import MainFamilyAffectedTrendViewHumanLoss from "./MainDashboardHumanLoss";
import MainRTIncidentDashboard from "./MainRtIncidentdashboard";
import HumanLossDougnut from "./HumanLossDashboard/DashboardCard/HumanLossTableDougnut";
import ManiHumanLossDougnut from "./MainDashboardHumanlossDoughnut";
import { Eye } from "react-bootstrap-icons";
import { Siren, AlertTriangle, FileText, ClipboardList, BarChart3, Activity, Phone, Users, CheckCircle2, Info, RefreshCw } from "lucide-react";
import {
  FaBolt,
  FaCalendar,
  FaCalendarAlt,
  FaClock,
  FaCloud,
  FaCloudRain,
  FaCloudSun,
  FaMapMarkerAlt,
  FaMoon,
  FaSmog,
  FaSnowflake,
  FaSun,
  FaUsers,
  FaChevronDown,
  FaChevronUp,
} from "react-icons/fa";
import DashboardMap from "./DashboardMap";

// Same allowedPermissionIds gate used for the sidebar (see navLinksData.js /
// Layout/Sidebar/NavItemHeader.js) so Quick Actions respects the same
// role-based permissions (SEOC, DEOC, Field Officer, etc.) as the nav menu.
const getUserPermissionIds = () => {
  try {
    return JSON.parse(localStorage.getItem("user_permissions") || "[]").map((p) => p.id);
  } catch {
    return [];
  }
};

const hasPermission = (allowedPermissionIds) => {
  if (!allowedPermissionIds || allowedPermissionIds.length === 0) return true;
  const userPermissionIds = getUserPermissionIds();
  return allowedPermissionIds.some((id) => userPermissionIds.includes(id));
};

// "Last Refreshed" stamp — DD-MM-YYYY hh:mm AM/PM, fed from the
// /config/server-time response so it reflects the server's clock.
const formatRefreshStamp = (value) => {
  if (!value) return "—";
  const d = value instanceof Date ? value : new Date(value);
  if (isNaN(d.getTime())) return "—";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yy = d.getFullYear();
  let h = d.getHours();
  const min = String(d.getMinutes()).padStart(2, "0");
  const ampm = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  return `${dd}-${mm}-${yy} ${String(h).padStart(2, "0")}:${min} ${ampm}`;
};

const SUMMARY_AUTO_REFRESH_MS = 10 * 60 * 1000;
// Default "From Date" for the Disaster Summary Cards section's date range —
// "To Date" always defaults to today, computed where it's used.
const DEFAULT_SUMMARY_FROM_DATE = "2025-01-01";

// Severity levels for the Incident Type Distribution stacked bars, ordered
// highest → lowest. `match` is the word the API puts in the level key
// (e.g. "Level 4 – Critical").
const INCIDENT_SEVERITY_META = [
  { match: "Critical", short: "Critical", color: "#B91C1C" },
  { match: "Major", short: "Major", color: "#EA580C" },
  { match: "Moderate", short: "Moderate", color: "#F59E0B" },
  { match: "Minor", short: "Minor", color: "#2563EB" },
];

const MainDashboard = () => {
  const handle = useFullScreenHandle();
  const [clickedKey, setClickedKey] = useState("");
  const [cardData, setCardData] = useState(null);
  const [currCardData, setCurrCardData] = useState(null);
  const [loader, setLoader] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  // Light/dark theme local to this dashboard only (see DashboardTheme.css) —
  // does not touch the app-wide theme used by the header/sidebar/other pages.
  const [odTheme, setOdTheme] = useState("light");
  const [fromDate, setFromDate] = useState();
  const [toDate, setToDate] = useState();
  const [horizontalChartTitle, setHorizontalChartTitle] = useState("");
  const [drildownAPIkey, setDrildownAPIkey] = useState("");
  const [drillDownData, setDrillDownData] = useState([]);
  const [trendData, setTrendData] = useState([]);
  const [affectedDistrictData, setAffectedDistrictData] = useState([]);
  const [affectedBlockData, setAffectedBlockData] = useState([]);
  const [affectedNagarNigamData, setAffectedNagarNigamData] = useState([]);
  const [affectedPanchayatData, setAffectedPanchayatData] = useState([]);
  const [activeTab, setActiveTab] = useState("dss");
  const [activeTabBmsk, setActiveTabBmsk] = useState("BMSK");
  const [activeKeyName, setactiveKeyName] = useState("");
  const [apiKeyName, setApiKeyName] = useState({});
  const [error, setError] = useState(false);
  const [selectedYear, setSelectedYear] = useState(2024);
  const [dateTime, setDateTime] = useState(new Date());
  const [location, setLocation] = useState("Fetching location...");
  const [weather, setWeather] = useState(null);
  const [LocationForDate, setLocationForDate] = useState(null);
  const [CityNameForAPI, setCityNameForAPI] = useState(null);
  const [dayName, setDayName] = useState("");

  // ── NEW: individual expand/collapse for each section (default collapsed) ──
  const [isCardsExpanded, setIsCardsExpanded] = useState(false);
  const [isGridExpanded, setIsGridExpanded] = useState(false);
  const [stateId, setStateId] = useState(null);
  const [dashboardSummary, setDashboardSummary] = useState(null);
  const [summaryLastRefreshed, setSummaryLastRefreshed] = useState(null);
  const [summaryRefreshing, setSummaryRefreshing] = useState(false);
  // Bumped on every summary Refresh / auto-refresh so the per-hazard cards
  // (MainDashboardCard: Floods / Avalanche / Cloud Burst) re-run their own
  // API calls too.
  const [summaryRefreshTick, setSummaryRefreshTick] = useState(0);
  // From/To Date picker for the Disaster Summary Cards section — deliberately
  // its own state, separate from the fromDate/toDate pair above (which
  // belongs to the unrelated, currently-dead Year-selector flow via
  // handleYearSelection) so this section's default view can't be disturbed
  // by that. Defaults to DEFAULT_SUMMARY_FROM_DATE → today; the *Input
  // fields hold the draft YYYY-MM-DD strings for the date inputs and only
  // commit to dsFromDate/dsToDate (which the APIs actually read) once
  // "Apply" is clicked.
  const [dsFromDate, setDsFromDate] = useState(() => new Date(DEFAULT_SUMMARY_FROM_DATE));
  const [dsToDate, setDsToDate] = useState(() => new Date());
  const [summaryFromDateInput, setSummaryFromDateInput] = useState(
    moment(DEFAULT_SUMMARY_FROM_DATE).format("YYYY-MM-DD")
  );
  const [summaryToDateInput, setSummaryToDateInput] = useState(moment().format("YYYY-MM-DD"));
  const [commLogSearch, setCommLogSearch] = useState("");
  const [commLogRoleFilter, setCommLogRoleFilter] = useState("");
  const [commLogRegionFilter, setCommLogRegionFilter] = useState("");
  const [commLogPage, setCommLogPage] = useState(1);
  const [commLogPageSize, setCommLogPageSize] = useState(5);
  // Floating tooltip for the Incident Type Distribution severity segments.
  const [itdTip, setItdTip] = useState(null);

  console.log(location, LocationForDate, "kktest");

  useEffect(() => {
    const fetchThemeData = async () => {
      try {
        const response = await getData(getThemeURL); // import getThemeURL from EndPoints
        if (response && response.length > 0) {
          setStateId(response[0].state_id);
        }
      } catch (error) {
        console.error("Error fetching theme data:", error);
      }
    };
    fetchThemeData();
  }, []);

  // Reloads everything shown in the "Disaster Summary Cards" section. Every
  // path — first load, manual Refresh and the 5-minute auto-refresh — runs
  // this and also hits /config/server-time so "Last Refreshed" reflects the
  // server clock. It refreshes in place, without the full-page loader.
  //
  // APIs re-called here:
  //   • /config/server-time                (Last Refreshed stamp)
  //   • dashboardSummaryData + stateId      (Incident Type Distribution,
  //                                          Incident Response Progress,
  //                                          Communication Logs)
  //   • getStateFloodCountData              (Floods card + area chart)
  //   • summaryRefreshTick bump → each MainDashboardCard re-fetches:
  //       - avalanchechartdata / cloudburstchartdata  (hazard charts)
  //       - avalanchecardsdata / cloudburstcardsdata / coldwavecardsdata
  //         (hazard "Human Loss" figures + sub-cards)
  const fetchDashboardSummary = useCallback(async () => {
    if (!stateId) return;
    setSummaryRefreshing(true);
    try {
      // Uses the section's committed From/To Date range (see the date
      // picker in the "Disaster Summary Cards" header) once the operator
      // has applied one; otherwise falls back to the default
      // DEFAULT_SUMMARY_FROM_DATE → today window.
      const rangeFromStr = dsFromDate ? moment(dsFromDate).format("YYYY-MM-DD") : DEFAULT_SUMMARY_FROM_DATE;
      const rangeToStr = dsToDate ? moment(dsToDate).format("YYYY-MM-DD") : moment().format("YYYY-MM-DD");
      const floodUrl = getStateFloodCountData
        .replace("FROMDATE", rangeFromStr)
        .replace("TODATE", rangeToStr);
      const [response, floodRes, timeRes] = await Promise.all([
        getData(`${dashboardSummaryData}${stateId}?from_date=${rangeFromStr}&to_date=${rangeToStr}`),
        getData(floodUrl).catch(() => null),
        getData(getServerDateAndTime).catch(() => null),
      ]);
      if (response) setDashboardSummary(response);
      if (Array.isArray(floodRes) && floodRes[0]) {
        const fd = { ...floodRes[0] };
        if (fd.families_gr_distributed) {
          fd.families_gr_distributed = `${Math.round(
            fd.families_gr_distributed / 7000
          )} (Rs: ${fd.families_gr_distributed})`;
        }
        setCardData(fd);
      }
      setSummaryLastRefreshed(
        timeRes && timeRes.serverTime ? new Date(timeRes.serverTime) : new Date()
      );
      setSummaryRefreshTick((n) => n + 1);
    } catch (error) {
      console.error("Error fetching dashboard summary:", error);
    } finally {
      setSummaryRefreshing(false);
    }
  }, [stateId, dsFromDate, dsToDate]);

  useEffect(() => {
    if (!stateId) return;
    fetchDashboardSummary();
  }, [stateId, fetchDashboardSummary]);

  useEffect(() => {
    if (!stateId) return undefined;
    const id = setInterval(fetchDashboardSummary, SUMMARY_AUTO_REFRESH_MS);
    return () => clearInterval(id);
  }, [stateId, fetchDashboardSummary]);

  // Commits the Disaster Summary Cards date-picker's draft inputs to
  // dsFromDate/dsToDate — fetchDashboardSummary's own useCallback deps pick
  // this up automatically (see its useEffect above) and re-fetch with the
  // new range; MainDashboardCard re-fetches too, since it receives
  // dsFromDate/dsToDate as its fromDate/toDate props.
  const handleApplySummaryDateRange = (e) => {
    e.stopPropagation();
    // A toast, not inline text — an error message sitting in this flex row
    // pushed/wrapped the whole header (title, inputs, Refresh) whenever it
    // appeared, so it can't live here.
    if (!summaryFromDateInput || !summaryToDateInput) {
      toast.error("Select both From and To dates.");
      return;
    }
    const from = new Date(summaryFromDateInput);
    const to = new Date(summaryToDateInput);
    if (from > to) {
      toast.error("From Date must be before To Date.");
      return;
    }
    setDsFromDate(from);
    setDsToDate(to);
  };

  // Reverts the date inputs and the committed range back to the section's
  // original default (DEFAULT_SUMMARY_FROM_DATE → today) — always available,
  // including to recover from an invalid draft (see handleApplySummaryDateRange).
  const handleClearSummaryDateRange = (e) => {
    e.stopPropagation();
    const defaultFrom = new Date(DEFAULT_SUMMARY_FROM_DATE);
    const defaultTo = new Date();
    setSummaryFromDateInput(moment(defaultFrom).format("YYYY-MM-DD"));
    setSummaryToDateInput(moment(defaultTo).format("YYYY-MM-DD"));
    setDsFromDate(defaultFrom);
    setDsToDate(defaultTo);
  };

  useEffect(() => {
    const updateDateTime = () => {
      const now = new Date();
      setDateTime(now);
      setDayName(now.toLocaleDateString("en-US", { weekday: "long" }));
    };

    updateDateTime();
    const interval = setInterval(updateDateTime, 60000);

    return () => clearInterval(interval);
  }, []);

  const GOOGLE_MAPS_API_KEY = "AIzaSyDLzC-FhQmB8JS2H46OW46DNOEboBbrZVg";

  const getPlaceNamefordate = async (latitude, longitude) => {
    try {
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=${GOOGLE_MAPS_API_KEY}`
      );
      const data = await response.json();

      if (data.status === "OK" && data.results && data.results.length > 0) {
        const addressComponents = data.results[0].address_components;

        let city = "";
        let district = "";
        let state = "";

        addressComponents.forEach((comp) => {
          if (comp.types.includes("locality")) city = comp.long_name;
          if (comp.types.includes("administrative_area_level_3")) {
            if (!city) city = comp.long_name;
          }
          if (comp.types.includes("administrative_area_level_2")) district = comp.long_name;
          if (comp.types.includes("administrative_area_level_1")) state = comp.long_name;
        });

        setLocationForDate(
          `${district || "District not found"}, ${state || "State not found"}`
        );
        setCityNameForAPI(city || district || "Unknown City");
      } else {
        setLocationForDate("Location not found");
        setCityNameForAPI("Unknown City");
      }
    } catch (error) {
      console.error("Error fetching location:", error);
      setLocationForDate("Error fetching location");
      setCityNameForAPI("Error fetching city");
    }
  };

  console.log(CityNameForAPI, "CityNameForAPI");

  const getPlaceName = async (latitude, longitude) => {
    try {
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=${GOOGLE_MAPS_API_KEY}`
      );
      const data = await response.json();

      if (data.status === "OK" && data.results && data.results.length > 0) {
        const addressComponents = data.results[0].address_components;
        let placeName = "";

        for (const comp of addressComponents) {
          if (comp.types.includes("locality")) {
            placeName = comp.long_name;
            break;
          }
          if (comp.types.includes("administrative_area_level_3") && !placeName) {
            placeName = comp.long_name;
          }
          if (comp.types.includes("sublocality_level_1") && !placeName) {
            placeName = comp.long_name;
          }
        }
        setLocation(placeName || "Unknown Location");
      } else {
        setLocation("Unknown Location");
      }
    } catch (error) {
      console.error("Error fetching location:", error);
      setLocation("Location not available");
    }
  };

  const [coords, setCoords] = useState(null);

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setCoords({ latitude, longitude });
          getPlaceName(latitude, longitude);
          getPlaceNamefordate(latitude, longitude);
        },
        (err) => {
          console.error("Geolocation denied:", err);
          setLocation("Location not available");
          setLocationForDate("Location not available");
        }
      );
    } else {
      setLocation("Geolocation not supported");
      setLocationForDate("Geolocation not supported");
    }
  }, []);

  useEffect(() => {
    if (!coords) return;

    const fetchWeather = async () => {
      try {
        const { latitude, longitude } = coords;

        const response = await fetch(
          `https://weather.googleapis.com/v1/currentConditions:lookup?key=${GOOGLE_MAPS_API_KEY}&location.latitude=${latitude}&location.longitude=${longitude}&unitsSystem=METRIC`
        );
        const data = await response.json();

        console.log("Google Weather API response:", data); // Debug log

        if (response.ok && data) {
          setWeather({
            temperature: data.temperature?.degrees,
            description: data.weatherCondition?.description?.text || "",
            humidity: data.relativeHumidity,
            windSpeed: data.wind?.speed?.value,
            precipitation: data.precipitation?.probability?.percent ?? 0,
            icon: data.weatherCondition?.type,
            isDaytime: data.isDaytime,
          });
        } else {
          console.error("Google Weather API error:", data);
        }
      } catch (error) {
        console.error("Error fetching Google weather data:", error);
      }
    };

    fetchWeather();

    // Refresh weather every 10 minutes
    const weatherInterval = setInterval(fetchWeather, 600000);
    return () => clearInterval(weatherInterval);
  }, [coords]);

  useEffect(() => {
    const interval = setInterval(() => setDateTime(new Date()), 60000);
    return () => clearInterval(interval);
  }, []);

  const getWeatherIcon = (conditionType, isDaytime = true) => {
    const iconMap = {
      CLEAR: { icon: isDaytime ? <FaSun /> : <FaMoon />, color: isDaytime ? "#FFD700" : "#A9A9A9" },
      PARTLY_CLOUDY: { icon: <FaCloudSun />, color: "#FFA500" },
      CLOUDY: { icon: <FaCloud />, color: "#B0C4DE" },
      OVERCAST: { icon: <FaCloud />, color: "#708090" },
      WINDY: { icon: <FaCloud />, color: "#87CEEB" },
      LIGHT_RAIN: { icon: <FaCloudRain />, color: "#1E90FF" },
      RAIN: { icon: <FaCloudRain />, color: "#1E90FF" },
      RAIN_SHOWERS: { icon: <FaCloudRain />, color: "#00BFFF" },
      HEAVY_RAIN: { icon: <FaCloudRain />, color: "#0000CD" },
      THUNDERSTORM: { icon: <FaBolt />, color: "#FFD700" },
      SNOW: { icon: <FaSnowflake />, color: "#00CED1" },
      LIGHT_SNOW: { icon: <FaSnowflake />, color: "#87CEEB" },
      FOG: { icon: <FaSmog />, color: "#708090" },
      HAZE: { icon: <FaSmog />, color: "#C0C0C0" },
    };

    const match = conditionType ? iconMap[conditionType] : null;
    return match ? (
      <span style={{ ...iconStyle, color: match.color }}>{match.icon}</span>
    ) : (
      <FaCloud style={{ ...iconStyle, color: "#B0C4DE" }} />
    );
  };

  const iconStyle = {
    marginRight: "5px",
    color: "red",
    fontSize: "24pt",
  };

  const { t } = useTranslation();
  const navigate = useNavigate();

  const quickActions = [
    { label: "Create Alert", icon: Siren, color: "#DC2626", path: "/Disaster-acknowledgereport/create-alert", allowedPermissionIds: [100] },
    { label: "Incident List", icon: AlertTriangle, color: "#F59E0B", path: "/situation-awareness/incident-management", allowedPermissionIds: [29] },
    { label: "Incident Facility Reports", icon: FileText, color: "#2563EB", path: "/reports/community-kitchen", allowedPermissionIds: [75] },
    { label: "Create SOP", icon: ClipboardList, color: "#7C3AED", path: "/Disaster-acknowledgereport/view-sop/create-sop", allowedPermissionIds: [36] },
  ].filter((action) => hasPermission(action.allowedPermissionIds));

  const fromdatenew = "2024-06-01";
  const todatenew = moment().format("YYYY-MM-DD");

  const aggregateData = (data) => {
    const aggregatedData = data.reduce((acc, current) => {
      const key = `${current.reported_on_date}-${current.name}-${current.id}`;

      if (!acc[key]) {
        acc[key] = {
          reported_on_date: current.reported_on_date,
          name: current.name,
          count: current.count,
          id: current.id,
          blk_name: [current.blk_name],
        };
      } else {
        acc[key].count += current.count;
        acc[key].blk_name.push(current.blk_name);
      }

      return acc;
    }, {});

    return Object.values(aggregatedData).map((item) => ({
      ...item,
      blk_name: item.blk_name.join(", "),
    }));
  };

  const navigateToGisManagement = () => {
    window.location.href =
      process.env.REACT_APP_GIS_SERVICE + "/gismanagement?incidentId=22";
  };

  const refreshCardDetailHoriChart = async (
    title,
    apiKey,
    key,
    apiKey2,
    keyName
  ) => {
    setApiKeyName({
      apiKey: apiKey,
      apiKey2: apiKey2,
      keyName: keyName,
      key: key,
    });
    setHorizontalChartTitle(title);
    setDrildownAPIkey(key);
    let startDateStr = moment(fromDate).format("YYYY-MM-DD");
    let endDateStr = moment(toDate).format("YYYY-MM-DD");
    let data;
    let data2;

    if (activeTab === "dss") {
      data = await getData(
        getAffectedDrilldownView
          .replace("API_KEY", apiKey)
          .replace("FROMDATE", fromdatenew)
          .replace("TODATE", todatenew)
      );
      data2 = await postDataWithBody(
        getCurrBarChartData + apiKey2,
        {
          fromdatenew: startDateStr,
          todatenew: endDateStr,
        },
        {}
      );
      setactiveKeyName(keyName);
    } else {
      data = await postDataWithBody(getCurrBarChartData + apiKey, {}, {});
      setactiveKeyName(keyName);
    }

    let customizedList = [
      "motor_boat",
      "private_boat",
      "government_boat",
      "dry_ration",
      "families_gr_distributed",
      "food_packets",
      "polythene_sheets",
      "health_centre",
      "community_kitchen",
    ];

    if (customizedList.indexOf(key) > -1) {
      if (data && data.length > 0) {
        for (let dataElem in data) {
          data[dataElem]["count"] = data[dataElem][key];
        }
      } else {
        data = [];
      }
    }
    const filtered = data.filter((item) => item.count > 0);
    if (data2?.length > 0 && keyName) {
      const updatedData2 = data2
        .map((item) => ({
          ...item,
          count: item[keyName],
        }))
        .filter((item) => item.count > 0);

      const result = aggregateData(updatedData2);
      setTrendData(result);
    } else if (data2?.length > 0) {
      const filtered2 = data2.filter((item) => item?.count > 0);
      const result = aggregateData(filtered2);
      setTrendData(result);
    } else {
      setTrendData([]);
    }
    setDrillDownData(filtered);
  };

  const refreshDashboard = async (startDateStr, endDateStr) => {
    startDateStr = moment(startDateStr).format("YYYY-MM-DD");
    endDateStr = moment(endDateStr).format("YYYY-MM-DD");
    setLoader(true);
    var data = await getData(
      getStateFloodCountData
        .replace("FROMDATE", fromdatenew)
        .replace("TODATE", todatenew)
    );
    setCardData(data[0]);
    data = data[0];
    if (data?.families_gr_distributed) {
      data.families_gr_distributed = `${Math.round(
        data.families_gr_distributed / 7000
      )} (Rs: ${data.families_gr_distributed})`;
    }
    data = await postDataWithBody(getCurrStateFloodCountData, {}, {});
    setCurrCardData(data[0]);
    let blockdata = [];
    data = await getData(
      getBlockWiseReport
        .replace("FROMDATE", fromdatenew)
        .replace("TODATE", todatenew)
    );
    data.forEach(function (data, index) {
      blockdata.push([data.count_date, data.block_count]);
    });

    // Fetch affected blocks data for Doughnut chart
    data = await getData(
      getAffectedBlock
        .replace("FROMDATE", fromdatenew)
        .replace("TODATE", todatenew)
    );
    setAffectedBlockData(data);

    // Fetch affected districts data for Doughnut chart
    data = await getData(
      getAffectedDistrict
        .replace("FROMDATE", fromdatenew)
        .replace("TODATE", todatenew)
    );

    setAffectedDistrictData(data);

    // Fetch affected NagarNigam data for Doughnut chart
    data = await getData(
      getAffectedNagarNigam
        .replace("FROMDATE", fromdatenew)
        .replace("TODATE", todatenew)
    );
    setAffectedNagarNigamData(data);

    // Fetch affected districts Panchayat for Doughnut chart
    data = await getData(
      getAffectedPanchayat
        .replace("FROMDATE", fromdatenew)
        .replace("TODATE", todatenew)
    );
    setAffectedPanchayatData(data);

    setLoader(false);
  };

  const refreshDashboardBtnClick = async () => {
    let startDateStr = moment(fromDate).format("YYYY-MM-DD");
    let endDateStr = moment(toDate).format("YYYY-MM-DD");
    setLoader(true);

    var data = await getData(
      getStateFloodCountData
        .replace("FROMDATE", fromdatenew)
        .replace("TODATE", todatenew)
    );

    setCardData(data[0]);
    data = data[0];
    data.families_gr_distributed = `${Math.round(
      data.families_gr_distributed / 7000
    )} (Rs: ${data.families_gr_distributed})`;
    data = await postDataWithBody(getCurrStateFloodCountData, {}, {});
    setCurrCardData(data[0]);

    if (activeTab === "dss") {
      let data = await getData(
        getAffectedDrilldownView
          .replace("API_KEY", apiKeyName.apiKey)
          .replace("FROMDATE", fromdatenew)
          .replace("TODATE", endDateStr)
      );
      let data2 = await postDataWithBody(
        getCurrBarChartData + apiKeyName.apiKey2,
        {
          fromDate: fromdatenew,
          toDate: todatenew,
        },
        {}
      );
      let customizedList = [
        "motor_boat",
        "private_boat",
        "government_boat",
        "dry_ration",
        "families_gr_distributed",
        "food_packets",
        "polythene_sheets",
        "health_centre",
        "community_kitchen",
        "people_remaining_in_camps",
        "animal_shelter",
      ];

      if (customizedList.indexOf(apiKeyName.key) > -1) {
        if (data && data.length > 0) {
          for (let dataElem in data) {
            data[dataElem]["count"] = data[dataElem][apiKeyName.key];
          }
        } else {
          data = [];
        }
      }
      const filtered = data.filter((item) => {
        return item.count > 0;
      });
      if (data2?.length > 0 && apiKeyName.keyName) {
        const updatedData2 = data2
          .map((item) => ({
            ...item,
            count: item[apiKeyName.keyName],
          }))
          .filter((item) => item.count > 0);
        const result = aggregateData(updatedData2);
        setTrendData(result);
      } else if (data2?.length > 0) {
        const filtered2 = data2.filter((item) => item?.count > 0);
        const result = aggregateData(filtered2);
        setTrendData(result);
      } else {
        setTrendData([]);
      }
      setDrillDownData(filtered);
    }

    let blockdata = [];
    data = await getData(
      getBlockWiseReport
        .replace("FROMDATE", fromdatenew)
        .replace("TODATE", todatenew)
    );
    data.forEach(function (data, index) {
      blockdata.push([data.count_date, data.block_count]);
    });

    // Fetch affected blocks data for Doughnut chart
    data = await getData(
      getAffectedBlock
        .replace("FROMDATE", fromdatenew)
        .replace("TODATE", todatenew)
    );
    setAffectedBlockData(data);

    // Fetch affected districts data for Doughnut chart
    data = await getData(
      getAffectedDistrict
        .replace("FROMDATE", fromdatenew)
        .replace("TODATE", todatenew)
    );

    setAffectedDistrictData(data);

    // Fetch affected NagarNigam data for Doughnut chart
    data = await getData(
      getAffectedNagarNigam
        .replace("FROMDATE", fromdatenew)
        .replace("TODATE", todatenew)
    );
    setAffectedNagarNigamData(data);

    // Fetch affected districts Panchayat for Doughnut chart
    data = await getData(
      getAffectedPanchayat
        .replace("FROMDATE", fromdatenew)
        .replace("TODATE", todatenew)
    );
    setAffectedPanchayatData(data);

    setLoader(false);
  };

  function getTrendChart() {
    return;
  }

  const dssDefaultApiCall = async () => {
    let startDate = moment(fromDate).format("YYYY-MM-DD");
    let endDate = moment(toDate).format("YYYY-MM-DD");
    setDrildownAPIkey("population_affected");
    setApiKeyName({
      apiKey: "getPopAffectedDetails",
      apiKey2: "trnd_population_affected",
    });

    const data = await getData(
      getAffectedDrilldownView
        .replace("API_KEY", "getPopAffectedDetails")
        .replace("FROMDATE", fromdatenew)
        .replace("TODATE", todatenew)
    );
    const data2 = await postDataWithBody(
      getCurrBarChartData + "trnd_population_affected",
      {
        fromDate: fromdatenew,
        toDate: todatenew,
      },
      {}
    );
    const filtered = data?.filter((item) => item.count > 0);
    const filtered2 = data2?.filter((item) => item.count > 0);
    setDrillDownData(filtered);
    setTrendData(filtered2);
  };

  const currDefaultApiCall = async () => {
    setApiKeyName({
      apiKey: "curr_fs_blocks_affected",
    });
    setDrildownAPIkey("curr_dss_affected_blocks");
    const data = await postDataWithBody(
      getCurrBarChartData + "curr_fs_blocks_affected",
      {},
      {}
    );
    const filtered = data?.filter((item) => item?.count > 0);
    setDrillDownData(filtered);
  };

  const toggleFullscreen = () => {
    // isFullscreen is kept in sync via <FullScreen onChange={setIsFullscreen}>,
    // which fires on every real fullscreen change — including the browser's own
    // Escape-key exit, not just clicks on this button.
    if (isFullscreen) {
      handle.exit();
    } else {
      handle.enter();
    }
  };

  const layoutDataBmsk = [
    {
      x: 0,
      y: 6,
      width: 12,
      height: 12,
      id: 6,
      component: <MainRTIncidentDashboard />,
    },
  ];

  const layoutData = [];
  const layoutData1 = [];

  const girdoptions = {
    staticGrid: true,
    column: 24,
    margin: 5,
  };

  useLayoutEffect(() => {
    var grid = GridStack.init(girdoptions);
  }, []);

  const layoutToDisplay = (() => {
    if (activeTab === "currentStatus") {
      return layoutData1;
    } else if (activeTabBmsk === "BMSK") {
      return layoutDataBmsk;
    } else if (activeTabBmsk === "Charts" || activeTab === "dss") {
      return layoutData;
    } else {
      return layoutData;
    }
  })();

  const getOrdinalSuffix = (day) => {
    if (day > 3 && day < 21) return "th";
    switch (day % 10) {
      case 1: return "st";
      case 2: return "nd";
      case 3: return "rd";
      default: return "th";
    }
  };

  const handleYearSelection = (selectedList) => {
    if (!selectedList || selectedList.length === 0) return;

    const selectedYear = parseInt(selectedList[0].name, 10);
    setSelectedYear(selectedYear);

    let fromDate, toDate;

    if (selectedYear == 2024) {
      fromDate = new Date(2024, 0, 1);
      toDate = new Date(2024, 11, 31);
    } else if (selectedYear == 2025) {
      fromDate = new Date(2025, 0, 1);
      toDate = new Date();
    }

    setFromDate(fromDate);
    setToDate(toDate);

    // Refresh the dashboard with the new date range
    refreshDashboard(fromDate, toDate);
  };

  useEffect(() => {
    // Set initial state on component load for 2024
    handleYearSelection([{ name: "2024" }]);
  }, []);

  useEffect(() => {
    if (fromDate && toDate && fromDate > toDate) {
      setError("Select proper From Date and To Date");
    } else {
      setError(null);
    }
  }, [fromDate, toDate]);

  // ── Reusable style builders for each section's toggle bar & panel ──
  const makeToggleBarStyle = (isExpanded) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    cursor: "pointer",
    padding: "8px 14px",
    marginTop: "10px",
    marginBottom: "0px",
    background: "linear-gradient(90deg, #0A2738 0%, #1a4a6e 100%)",
    borderRadius: isExpanded ? "8px 8px 0 0" : "8px",
    color: "#fff",
    userSelect: "none",
    boxShadow: "0 2px 6px rgba(10,39,56,0.18)",
    transition: "border-radius 0.2s",
  });

  const makeCollapsibleStyle = (isExpanded) => ({
    overflow: "hidden",
    maxHeight: isExpanded ? "10000px" : "0px",
    opacity: isExpanded ? 1 : 0,
    transition: "max-height 0.45s ease, opacity 0.3s ease",
    border: isExpanded ? "1px solid var(--od-border)" : "none",
    borderTop: "none",
    borderRadius: "0 0 8px 8px",
    background: "var(--od-card-bg)",
  });

  return (
    <FullScreen handle={handle} onChange={setIsFullscreen}>
      <div
        className={
          isFullscreen
            ? "state-dashboard_div fullscreen"
            : "state-dashboard_div"
        }
        data-od-theme={odTheme}
        style={{
          height: isFullscreen ? "100vh" : "auto",
          overflow: isFullscreen ? "auto" : "initial",
          // In dark mode, bleed over the surrounding .content wrapper's own
          // padding (see src/index.css) so its fixed light-gray background
          // doesn't show as a thin mismatched edge around the dark card.
          // Padding is grown by the same amount the negative margin eats,
          // so the visible inset stays the same as in light mode.
          margin: !isFullscreen && odTheme === "dark" ? "-6px -12px -20px -12px" : 0,
          padding: !isFullscreen && odTheme === "dark" ? "16px 22px 30px 22px" : "10px",
        }}
      >
        <Loader
          show={loader}
          type="body"
          stack="vertical"
          message="Loading Data"
        />

        <div
          style={{
            display: "flex",
            alignItems: "center",
            marginBottom: "-28pt",
            width: "100%",
          }}
        >
          <p
            style={{
              textAlign: "left",
              marginTop: "0pt",
              marginRight: "114pt",
              marginLeft: "447pt",
              color: "var(--od-text-muted)",
              fontWeight: "bold",
              whiteSpace: "nowrap",
            }}
          >
            {/* {LocationForDate} */}
          </p>
        </div>

        <div className="container-fluid">
          <div className="toprow--container">
            <div className="dash-title" style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", lineHeight: 1.15 }}>
              <span style={{ fontSize: "1rem", fontWeight: 800, letterSpacing: "0.5px", whiteSpace: "nowrap" }}>Operational Dashboard</span>
            </div>
            <div
              style={{
                marginLeft: "288px",
                marginBottom: "0px",
                textAlign: "right",
                marginTop: "0pt",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
                <FaCalendar
                  style={{
                    marginRight: "4pt",
                    color: "var(--od-text-primary)",
                    fontWeight: "bold",
                    flexShrink: 0,
                  }}
                />
                <span
                  style={{ fontSize: "1rem", color: "var(--od-text-primary)", fontWeight: "600", whiteSpace: "nowrap" }}
                >
                  {dayName}, {dateTime.getDate()}
                  {getOrdinalSuffix(dateTime.getDate())}{" "}
                  {dateTime.toLocaleDateString("en-US", { month: "long" })}{" "}
                  {dateTime.getFullYear()}
                </span>
              </div>
              {LocationForDate && (
                <span
                  style={{
                    fontSize: "0.75rem",
                    color: "var(--od-text-muted)",
                    fontWeight: "500",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 4,
                    marginTop: 2,
                  }}
                >
                  📍 {location}
                </span>
              )}
            </div>
            <div
              style={{
                marginTop: "15pt",
                color: "var(--od-text-primary)",
                fontWeight: "bold",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                padding: "10px",
                borderRadius: "8px",
                width: "300px",
              }}
            >
              {weather && (
                <>
                  <div
                    style={{
                      display: "flex",
                      marginLeft: "3px",
                      marginTop: "-13px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        marginRight: "10pt",
                      }}
                    >
                      <span>{getWeatherIcon(weather.icon, weather.isDaytime)}</span>
                      <span style={{ fontSize: "15pt", marginLeft: "8px" }}>
                        {weather.temperature}
                      </span>
                      <span style={{ marginTop: "-10px" }}>°C</span>
                    </div>
                    <div
                      style={{
                        fontSize: "10px",
                        marginTop: "4px",
                        textAlign: "right",
                        display: "inline",
                        color: "var(--od-text-muted)",
                      }}
                    >
                      <p style={{ marginBottom: "0pt", letterSpacing: "0.6px" }}>
                        Precipitation: {weather.precipitation}%
                      </p>
                      <p style={{ marginBottom: "0pt", letterSpacing: "0.6px" }}>
                        Humidity: {weather.humidity}%
                      </p>
                      <p style={{ marginBottom: "0pt", letterSpacing: "0.6px" }}>
                        Wind: {weather.windSpeed}km/h
                      </p>
                    </div>
                  </div>
                </>
              )}
            </div>

            <div style={{ alignSelf: "flex-start", display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
              <button
                onClick={toggleFullscreen}
                title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 8,
                  flexShrink: 0,
                  background: "rgba(37,99,235,0.08)",
                  border: "1px solid rgba(37,99,235,0.22)",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "background 0.2s, border-color 0.2s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "rgba(37,99,235,0.15)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "rgba(37,99,235,0.08)";
                }}
              >
                {isFullscreen ? (
                  <svg viewBox="0 0 18 18" fill="none" width="14" height="14">
                    <path d="M2 6H6V2M16 6H12V2M2 12H6V16M16 12H12V16" stroke="#2563EB" strokeWidth="1.6" strokeLinecap="round" />
                  </svg>
                ) : (
                  <svg viewBox="0 0 18 18" fill="none" width="14" height="14">
                    <path d="M2 6V2h4M12 2h4v4M2 12v4h4M16 12v4h-4" stroke="#2563EB" strokeWidth="1.6" strokeLinecap="round" />
                  </svg>
                )}
              </button>

              {/* Theme toggle — local to this dashboard only, see odTheme/DashboardTheme.css */}
              <button
                onClick={() => setOdTheme((prev) => (prev === "light" ? "dark" : "light"))}
                title={odTheme === "dark" ? "Switch to Light Theme" : "Switch to Dark Theme"}
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 8,
                  flexShrink: 0,
                  background: "rgba(37,99,235,0.08)",
                  border: "1px solid rgba(37,99,235,0.22)",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "background 0.2s, border-color 0.2s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "rgba(37,99,235,0.15)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "rgba(37,99,235,0.08)";
                }}
              >
                {odTheme === "dark" ? (
                  <svg viewBox="0 0 18 18" fill="none" width="14" height="14">
                    <circle cx="9" cy="9" r="3.6" stroke="#2563EB" strokeWidth="1.6" />
                    <path d="M9 1.5v2M9 14.5v2M1.5 9h2M14.5 9h2M3.6 3.6l1.4 1.4M13 13l1.4 1.4M14.4 3.6L13 5M5 13l-1.4 1.4" stroke="#2563EB" strokeWidth="1.6" strokeLinecap="round" />
                  </svg>
                ) : (
                  <svg viewBox="0 0 18 18" fill="none" width="14" height="14">
                    <path d="M15.5 10.3A6.5 6.5 0 0 1 7.7 2.5a6.5 6.5 0 1 0 7.8 7.8Z" stroke="#2563EB" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </button>
            </div>
          </div>

          {error && <div className="error-message">{error}</div>}

          {/* Map is always visible */}
          <DashboardMap height="500px" />

          {/* ══════════════════════════════════════════════
               SECTION 1 — Dashboard Cards
               (Floods / Avalanche / Cloud Burst / Glof)
          ══════════════════════════════════════════════ */}
          <div
            style={{
              background: "var(--od-card-bg)",
              border: "1px solid var(--od-border)",
              borderRadius: 12,
              boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
              marginTop: 10,
              overflow: "hidden",
            }}
          >
            {/* Header */}
            <div
              onClick={() => setIsCardsExpanded((prev) => !prev)}
              style={{
                padding: "10px 14px",
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                rowGap: 8,
                gap: 8,
                background: "var(--od-surface-alt)",
                borderBottom: isCardsExpanded ? "1px solid var(--od-border)" : "1px solid transparent",
                borderRadius: "12px 12px 0 0",
                transition: "border-color 0.35s ease",
                minHeight: 52,
                cursor: "pointer",
                userSelect: "none",
              }}
            >
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 8,
                  flexShrink: 0,
                  background: "rgba(239, 68, 68, 0.12)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 16,
                  // boxShadow: "0 2px 8px rgba(220,38,38,0.3)",
                }}
              >
                🚨
              </div>

              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: "var(--od-text-primary)", letterSpacing: "0.04em" }}>
                  Disaster Summary Cards
                </div>
                {/* <div style={{ fontSize: 11, color: "var(--od-text-muted)" }}>
                  {isCardsExpanded ? "Click to collapse" : "Click to expand"}
                </div> */}
              </div>

              {/* Disaster Summary Cards' own date range — separate from any
                  other date picker on this page (see dsFromDate/dsToDate) —
                  drives the Floods trend chart and the Avalanche/Cloud Burst
                  cards below once Applied. */}
              <div
                onClick={(e) => e.stopPropagation()}
                style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}
              >
                <input
                  type="date"
                  className="ds-date-input"
                  value={summaryFromDateInput}
                  onChange={(e) => setSummaryFromDateInput(e.target.value)}
                  title="From Date"
                  style={{
                    width: 140,
                    minWidth: 140,
                    flexShrink: 0,
                    height: 30,
                    boxSizing: "border-box",
                    display: "flex",
                    alignItems: "center",
                    padding: "0 10px 0 8px",
                    borderRadius: 7,
                    border: "1px solid var(--od-border)",
                    background: "var(--od-card-bg)",
                    color: "var(--od-text-primary)",
                    fontSize: 11.5,
                  }}
                />
                <span style={{ fontSize: 11, color: "var(--od-text-muted)" }}>to</span>
                <input
                  type="date"
                  className="ds-date-input"
                  value={summaryToDateInput}
                  onChange={(e) => setSummaryToDateInput(e.target.value)}
                  title="To Date"
                  style={{
                    width: 140,
                    minWidth: 140,
                    flexShrink: 0,
                    height: 30,
                    boxSizing: "border-box",
                    display: "flex",
                    alignItems: "center",
                    padding: "0 10px 0 8px",
                    borderRadius: 7,
                    border: "1px solid var(--od-border)",
                    background: "var(--od-card-bg)",
                    color: "var(--od-text-primary)",
                    fontSize: 11.5,
                  }}
                />
                <button
                  onClick={handleApplySummaryDateRange}
                  disabled={summaryRefreshing}
                  title="Apply date range"
                  style={{
                    height: 30,
                    padding: "0 12px",
                    borderRadius: 7,
                    flexShrink: 0,
                    background: "#2563eb",
                    border: "1px solid #2563eb",
                    color: "#fff",
                    fontSize: 11.5,
                    fontWeight: 700,
                    cursor: summaryRefreshing ? "not-allowed" : "pointer",
                  }}
                >
                  Apply
                </button>
                {(() => {
                  const todayStr = moment().format("YYYY-MM-DD");
                  // Enabled whenever EITHER the draft inputs or the last
                  // committed range differ from the default — covers a
                  // failed Apply too (draft shows the bad values but
                  // dsFromDate/dsToDate never moved from default), so Clear
                  // is always available to recover from that.
                  const draftIsDefault =
                    summaryFromDateInput === DEFAULT_SUMMARY_FROM_DATE && summaryToDateInput === todayStr;
                  const committedIsDefault =
                    moment(dsFromDate).format("YYYY-MM-DD") === DEFAULT_SUMMARY_FROM_DATE &&
                    moment(dsToDate).format("YYYY-MM-DD") === todayStr;
                  const isSummaryRangeDefault = draftIsDefault && committedIsDefault;
                  const clearDisabled = summaryRefreshing || isSummaryRangeDefault;
                  return (
                    <button
                      onClick={handleClearSummaryDateRange}
                      disabled={clearDisabled}
                      title={isSummaryRangeDefault ? "Already showing the default date range" : "Reset to the default date range"}
                      style={{
                        height: 30,
                        padding: "0 12px",
                        borderRadius: 7,
                        flexShrink: 0,
                        background: "var(--od-card-bg)",
                        border: "1px solid var(--od-border)",
                        color: clearDisabled ? "var(--od-text-muted)" : "var(--od-text-secondary)",
                        opacity: clearDisabled ? 0.55 : 1,
                        fontSize: 11.5,
                        fontWeight: 700,
                        cursor: clearDisabled ? "not-allowed" : "pointer",
                      }}
                    >
                      Clear
                    </button>
                  );
                })()}
              </div>

              {/* Refresh status/controls — visually separated from the date
                  filters above and pinned to the far right. */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  marginLeft: "auto",
                  paddingLeft: 12,
                  borderLeft: "1px solid var(--od-border)",
                }}
              >
              <span
                title="Auto-refreshes every 10 minutes"
                onClick={(e) => e.stopPropagation()}
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: "var(--od-text-muted)",
                  whiteSpace: "nowrap",
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                  cursor: "default",
                }}
              >
                {summaryRefreshing && (
                  <RefreshCw size={11} className="ds-refresh-spin" color="#2563eb" />
                )}
                Last Refreshed: {summaryLastRefreshed ? formatRefreshStamp(summaryLastRefreshed) : "—"}
              </span>

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  fetchDashboardSummary();
                }}
                disabled={summaryRefreshing}
                title="Refresh disaster summary"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 5,
                  height: 30,
                  padding: "0 10px",
                  borderRadius: 7,
                  flexShrink: 0,
                  background: "rgba(37,99,235,0.08)",
                  border: "1px solid rgba(37,99,235,0.22)",
                  color: "#2563eb",
                  fontSize: 11.5,
                  fontWeight: 700,
                  cursor: summaryRefreshing ? "not-allowed" : "pointer",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "rgba(37,99,235,0.15)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "rgba(37,99,235,0.08)";
                }}
              >
                <RefreshCw size={12} className={summaryRefreshing ? "ds-refresh-spin" : ""} />
                Refresh
              </button>

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setIsCardsExpanded((prev) => !prev);
                }}
                title={isCardsExpanded ? "Collapse cards" : "Expand cards"}
                style={{
                  width: 30,
                  height: 30,
                  borderRadius: 7,
                  flexShrink: 0,
                  background: isCardsExpanded ? "rgba(220,38,38,0.08)" : "rgba(239,68,68,0.08)",
                  border: `1px solid ${isCardsExpanded ? "rgba(220,38,38,0.22)" : "rgba(239,68,68,0.22)"}`,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "background 0.2s, border-color 0.2s",
                  padding: 0,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "rgba(220,38,38,0.15)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = isCardsExpanded ? "rgba(220,38,38,0.08)" : "rgba(239,68,68,0.08)";
                }}
              >
                <svg
                  className={`ds-chevron-icon ${isCardsExpanded ? "open" : "closed"}`}
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  fill="none"
                >
                  <path
                    d="M2 4.5L6 8.5L10 4.5"
                    stroke="#DC2626"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              </div>
            </div>

            {/* Collapsible Body */}
            <div style={makeCollapsibleStyle(isCardsExpanded)}>
              <div style={{ padding: "10px 14px 4px" }}>
                {/* ── Floods / Avalanche / Cloud Burst cards ── */}
                <div className="dashboard_card-container_1" style={{ marginBottom: 12 }}>
                  {maindashboardCardConfig
                    ?.filter((card) => card.title !== "Cold Wave")
                    .map((card, idx) => (
                    <MainDashboardCard
                      key={idx}
                      config={card}
                      clickedKey={clickedKey}
                      setClickedKey={setClickedKey}
                      data={cardData}
                      triggeredFunction={refreshCardDetailHoriChart}
                      index={0}
                      fromDate={dsFromDate}
                      toDate={dsToDate}
                      stateId={stateId}
                      refreshSignal={summaryRefreshTick}
                    />
                  ))}
                </div>

                {/* ── Quick Actions + Incident Type Distribution ── */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1.4fr", gap: 12, marginBottom: 12, alignItems: "stretch" }}>
                  <div style={{ background: "var(--od-card-bg)", border: "1px solid var(--od-border)", borderRadius: 10, overflow: "hidden" }}>
                    <div style={{ padding: "8px 12px", display: "flex", alignItems: "center", gap: 8, background: "var(--od-surface-alt)", borderBottom: "1px solid var(--od-border)" }}>
                      <div style={{ width: 26, height: 26, borderRadius: 7, background: "rgba(37,99,235,0.12)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13 }}>⚡</div>
                      <div style={{ fontSize: 12, fontWeight: 700, color: "var(--od-text-primary)", letterSpacing: "0.04em" }}>Quick Actions</div>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 8, padding: "10px" }}>
                      {quickActions.map((action) => (
                        <button
                          key={action.label}
                          onClick={() => navigate(action.path)}
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: 5,
                            padding: "10px 6px",
                            background: "var(--od-card-bg)",
                            border: "1px solid var(--od-border)",
                            borderRadius: 8,
                            cursor: "pointer",
                            transition: "box-shadow 0.2s, transform 0.15s, border-color 0.2s",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.1)";
                            e.currentTarget.style.transform = "translateY(-2px)";
                            e.currentTarget.style.borderColor = action.color;
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.boxShadow = "none";
                            e.currentTarget.style.transform = "translateY(0)";
                            e.currentTarget.style.borderColor = "var(--od-border)";
                          }}
                        >
                          <div
                            style={{
                              width: 32,
                              height: 32,
                              borderRadius: 8,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              background: `${action.color}1f`,
                            }}
                          >
                            <action.icon size={15} color={action.color} />
                          </div>
                          <span style={{ fontSize: 11, fontWeight: 600, color: "var(--od-text-primary)", textAlign: "center" }}>
                            {action.label}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {(() => {
                    const hazardData = dashboardSummary?.incidentHazardTypeCounts || {};
                    // The API returns each hazard either as a flat count or as
                    // an object { total, "Level 4 – Critical": n, ... }. Take
                    // `total` for the bar length and split the level counts
                    // into ordered severity segments for the stacked bar.
                    const entries = Object.entries(hazardData)
                      .filter(([key]) => key !== "totalIncidentsCount")
                      .map(([key, v]) => {
                        const isObj = v !== null && typeof v === "object";
                        const count = isObj ? Number(v.total) || 0 : Number(v) || 0;
                        const segments = isObj
                          ? INCIDENT_SEVERITY_META.map((s) => {
                              const lvlKey = Object.keys(v).find(
                                (k) => k !== "total" && k.includes(s.match)
                              );
                              return { ...s, value: lvlKey ? Number(v[lvlKey]) || 0 : 0 };
                            }).filter((s) => s.value > 0)
                          : [];
                        return { label: key, value: count, segments };
                      })
                      .sort((a, b) => b.value - a.value);
                    const maxVal = entries.length ? Math.max(...entries.map((e) => e.value)) : 0;
                    const niceMax = maxVal <= 0 ? 4 : Math.ceil(maxVal / 4) * 4;
                    const usedSeverities = INCIDENT_SEVERITY_META.filter((s) =>
                      entries.some((e) => e.segments.some((seg) => seg.match === s.match))
                    );
                    const totalIncidents =
                      hazardData.totalIncidentsCount != null
                        ? Number(hazardData.totalIncidentsCount) || 0
                        : entries.reduce((sum, e) => sum + e.value, 0);
                    return (
                      <div style={{ background: "var(--od-card-bg)", border: "1px solid var(--od-border)", borderRadius: 10, padding: "12px 16px", height: "100%", display: "flex", flexDirection: "column", boxSizing: "border-box" }}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 14, flexWrap: "wrap" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <BarChart3 size={16} color="#2563EB" />
                            <span style={{ fontSize: 13, fontWeight: 700, color: "var(--od-text-primary)" }}>Incident Type Distribution</span>
                            <span
                              style={{
                                fontSize: 10.5,
                                fontWeight: 700,
                                color: "#2563EB",
                                background: "rgba(37,99,235,0.1)",
                                border: "1px solid rgba(37,99,235,0.22)",
                                borderRadius: 999,
                                padding: "2px 8px",
                                whiteSpace: "nowrap",
                              }}
                            >
                              Total Incidents: {totalIncidents}
                            </span>
                          </div>
                          {usedSeverities.length > 0 && (
                            <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 12px" }}>
                              {usedSeverities.map((s) => (
                                <div key={s.match} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                                  <span style={{ width: 9, height: 9, borderRadius: 2, background: s.color, flexShrink: 0 }} />
                                  <span style={{ fontSize: 10, color: "var(--od-text-muted)", fontWeight: 600 }}>{s.short}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                        <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center" }}>
                        {entries.length === 0 ? (
                          <div style={{ fontSize: 12, color: "var(--od-text-muted)", padding: "8px 0" }}>No incident data available</div>
                        ) : (
                          <>
                            {entries.map(({ label, value, segments }) => (
                              <div key={label} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                                <div style={{ width: 90, flexShrink: 0, fontSize: 12, color: "var(--od-text-secondary)", textAlign: "right", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{label}</div>
                                <div style={{ flex: 1, position: "relative", height: 16, background: "var(--od-surface-alt)", borderRadius: 4 }}>
                                  <div
                                    style={{
                                      display: "flex",
                                      width: `${niceMax ? (value / niceMax) * 100 : 0}%`,
                                      height: "100%",
                                      borderRadius: 4,
                                      overflow: "hidden",
                                      transition: "width 0.4s ease",
                                    }}
                                  >
                                    {segments.length > 0 ? (
                                      segments.map((seg) => (
                                        <div
                                          key={seg.match}
                                          onMouseEnter={(e) =>
                                            setItdTip({
                                              text: `${label} — ${seg.short}: ${seg.value}`,
                                              x: e.clientX,
                                              y: e.clientY,
                                            })
                                          }
                                          onMouseMove={(e) =>
                                            setItdTip({
                                              text: `${label} — ${seg.short}: ${seg.value}`,
                                              x: e.clientX,
                                              y: e.clientY,
                                            })
                                          }
                                          onMouseLeave={() => setItdTip(null)}
                                          style={{
                                            flexGrow: seg.value,
                                            flexBasis: 0,
                                            minWidth: 0,
                                            background: seg.color,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            fontSize: 9.5,
                                            fontWeight: 700,
                                            color: "#fff",
                                            overflow: "hidden",
                                            cursor: "default",
                                          }}
                                        >
                                          {seg.value}
                                        </div>
                                      ))
                                    ) : (
                                      <div style={{ flex: 1, background: "#2563EB" }} />
                                    )}
                                  </div>
                                </div>
                                <div style={{ width: 28, flexShrink: 0, fontSize: 12, fontWeight: 700, color: "#2563EB" }}>{value}</div>
                              </div>
                            ))}
                          </>
                        )}
                        </div>
                        {itdTip && (
                          <div
                            style={{
                              position: "fixed",
                              left: Math.min(itdTip.x + 14, window.innerWidth - 200),
                              top: itdTip.y + 16,
                              background: "#111827",
                              color: "#fff",
                              fontSize: 11,
                              fontWeight: 600,
                              padding: "4px 8px",
                              borderRadius: 6,
                              whiteSpace: "nowrap",
                              pointerEvents: "none",
                              zIndex: 9999,
                              boxShadow: "0 4px 14px rgba(0,0,0,0.2)",
                            }}
                          >
                            {itdTip.text}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>

                {/* ── Incident Response Progress + Communication Logs ── */}
                <div style={{ display: "grid", gridTemplateColumns: "320px 1fr", gap: 12, marginBottom: 12, alignItems: "stretch" }}>
                  {(() => {
                    const sw = dashboardSummary?.stateWiseCounts || {};
                    const progressRows = [
                      { label: "Incidents Reported", value: sw.incidentsCount || 0, icon: AlertTriangle, color: "#F59E0B" },
                      {
                        label: "SOPs Executed",
                        value: sw.sopsExecutedCount || 0,
                        icon: ClipboardList,
                        color: "#7C3AED",
                        tooltip: "Multiple SOPs can be executed for a single incident, so this count may exceed the number of incidents reported.",
                      },
                      { label: "Resources Deployed", value: sw.resourceDeployedCount || 0, icon: Users, color: "#2563EB" },
                      { label: "Resources Released", value: sw.resourceReleasedCount || 0, icon: CheckCircle2, color: "#16A34A" },
                    ];
                    const maxVal = Math.max(1, ...progressRows.map((row) => row.value));
                    return (
                      <div style={{ background: "var(--od-card-bg)", border: "1px solid var(--od-border)", borderRadius: 10, padding: "12px 16px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
                          <Activity size={16} color="#16A34A" />
                          <span style={{ fontSize: 13, fontWeight: 700, color: "var(--od-text-primary)" }}>Incident Response Progress</span>
                        </div>
                        {progressRows.map((row) => (
                          <div key={row.label} style={{ marginBottom: 12 }}>
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
                              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                <row.icon size={13} color={row.color} />
                                <span style={{ fontSize: 12, color: "var(--od-text-secondary)", fontWeight: 600 }}>{row.label}</span>
                                {row.tooltip && (
                                  <span className="irp-info-wrap">
                                    <Info size={12} color="var(--od-text-muted)" style={{ cursor: "default" }} />
                                    <span className="irp-info-tip">{row.tooltip}</span>
                                  </span>
                                )}
                              </div>
                              <span style={{ fontSize: 12, fontWeight: 700, color: "var(--od-text-primary)" }}>{row.value}</span>
                            </div>
                            <div style={{ width: "100%", height: 7, background: "var(--od-surface-alt)", borderRadius: 4 }}>
                              <div
                                style={{
                                  width: `${(row.value / maxVal) * 100}%`,
                                  height: "100%",
                                  background: row.color,
                                  borderRadius: 4,
                                  transition: "width 0.4s ease",
                                }}
                              />
                            </div>
                          </div>
                        ))}
                        <div
                          style={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: "8px 14px",
                            marginTop: 4,
                            paddingTop: 10,
                            borderTop: "1px solid var(--od-border)",
                          }}
                        >
                          {progressRows.map((row) => (
                            <div key={`legend-${row.label}`} style={{ display: "flex", alignItems: "center", gap: 5 }}>
                              <span style={{ width: 8, height: 8, borderRadius: "50%", background: row.color, flexShrink: 0 }} />
                              <span style={{ fontSize: 10.5, color: "var(--od-text-muted)", fontWeight: 500 }}>{row.label}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })()}

                  {(() => {
                    const allLogs = dashboardSummary?.communicationChannel || [];
                    const columns = [
                      { key: "name", label: "Name", color: "var(--od-text-secondary)", weight: 700 },
                      { key: "role_names", label: "Role Names", color: "#2563EB", weight: 700 },
                      { key: "region_names", label: "Region Names", color: "var(--od-text-secondary)", weight: 400 },
                      { key: "phone_number", label: "Phone Number", color: "var(--od-text-primary)", weight: 600 },
                      { key: "email", label: "Email", color: "var(--od-text-muted)", weight: 400 },
                    ];
                    const gridCols = "1.2fr 1fr 1fr 1fr 1.6fr";

                    const roleOptions = Array.from(
                      new Set(allLogs.map((l) => l.role_names).filter(Boolean))
                    ).sort();
                    const regionOptions = Array.from(
                      new Set(
                        allLogs.flatMap((l) => (l.region_names || "").split(",").map((r) => r.trim())).filter(Boolean)
                      )
                    ).sort();

                    const searchLower = commLogSearch.trim().toLowerCase();
                    const logs = allLogs.filter((log) => {
                      const matchesSearch =
                        !searchLower ||
                        [log.name, log.role_names, log.region_names].some((v) =>
                          (v || "").toLowerCase().includes(searchLower)
                        );
                      const matchesRole = !commLogRoleFilter || log.role_names === commLogRoleFilter;
                      const matchesRegion =
                        !commLogRegionFilter ||
                        (log.region_names || "").split(",").map((r) => r.trim()).includes(commLogRegionFilter);
                      return matchesSearch && matchesRole && matchesRegion;
                    });

                    const hasActiveFilters = !!(commLogSearch || commLogRoleFilter || commLogRegionFilter);

                    // Pagination — derived from the filtered set, clamped so a
                    // filter change or a shrinking result set never strands the
                    // view on a page that no longer exists.
                    const totalLogCount = logs.length;
                    const totalLogPages = Math.max(1, Math.ceil(totalLogCount / commLogPageSize));
                    const currentLogPage = Math.min(commLogPage, totalLogPages);
                    const logPageStart = (currentLogPage - 1) * commLogPageSize;
                    const pagedLogs = logs.slice(logPageStart, logPageStart + commLogPageSize);

                    const resetToFirstPage = () => setCommLogPage(1);

                    const selectStyle = {
                      fontSize: 11,
                      padding: "5px 8px",
                      border: "1px solid var(--od-border)",
                      borderRadius: 6,
                      background: "var(--od-surface-alt)",
                      color: "var(--od-text-secondary)",
                      outline: "none",
                    };
                    return (
                      <div style={{ background: "var(--od-card-bg)", border: "1px solid var(--od-border)", borderRadius: 10, padding: "12px 16px", display: "flex", flexDirection: "column" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                          <Phone size={15} color="#2563EB" />
                          <span style={{ fontSize: 13, fontWeight: 700, color: "var(--od-text-primary)" }}>Communication Logs</span>
                        </div>

                        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8, marginBottom: 10 }}>
                          <input
                            type="text"
                            placeholder="Search by Name, Role, or Region..."
                            value={commLogSearch}
                            onChange={(e) => { setCommLogSearch(e.target.value); resetToFirstPage(); }}
                            style={{ ...selectStyle, flex: "1 1 200px", minWidth: 160 }}
                          />
                          <select
                            value={commLogRoleFilter}
                            onChange={(e) => { setCommLogRoleFilter(e.target.value); resetToFirstPage(); }}
                            style={{ ...selectStyle, flex: "0 0 auto" }}
                          >
                            <option value="">All Roles</option>
                            {roleOptions.map((role) => (
                              <option key={role} value={role}>{role}</option>
                            ))}
                          </select>
                          <select
                            value={commLogRegionFilter}
                            onChange={(e) => { setCommLogRegionFilter(e.target.value); resetToFirstPage(); }}
                            style={{ ...selectStyle, flex: "0 0 auto" }}
                          >
                            <option value="">All Regions</option>
                            {regionOptions.map((region) => (
                              <option key={region} value={region}>{region}</option>
                            ))}
                          </select>
                          {hasActiveFilters && (
                            <button
                              onClick={() => {
                                setCommLogSearch("");
                                setCommLogRoleFilter("");
                                setCommLogRegionFilter("");
                                resetToFirstPage();
                              }}
                              style={{
                                fontSize: 11, fontWeight: 600, color: "#ef4444",
                                background: "transparent", border: "none", cursor: "pointer", padding: "2px 4px",
                              }}
                            >
                              Clear
                            </button>
                          )}
                          <span style={{ fontSize: 10.5, color: "var(--od-text-muted)", marginLeft: "auto", whiteSpace: "nowrap" }}>
                            {totalLogCount === 0
                              ? `Showing 0 of ${allLogs.length} contacts`
                              : `Showing ${logPageStart + 1}-${Math.min(logPageStart + commLogPageSize, totalLogCount)} of ${totalLogCount} contacts`}
                          </span>
                        </div>

                        <div style={{ overflowX: "auto" }}>
                          <div style={{ minWidth: 580 }}>
                            <div
                              style={{
                                display: "grid",
                                gridTemplateColumns: gridCols,
                                columnGap: 8,
                                padding: "0 6px 6px",
                                borderBottom: "1px solid var(--od-border)",
                              }}
                            >
                              {columns.map((col) => (
                                <div
                                  key={col.key}
                                  style={{
                                    color: "var(--od-text-muted)",
                                    fontWeight: 700,
                                    fontSize: 10,
                                    letterSpacing: "0.03em",
                                    textTransform: "uppercase",
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                  }}
                                >
                                  {col.label}
                                </div>
                              ))}
                            </div>
                            {/* Fixed height (not maxHeight) so the card's overall
                                height stays constant across every rows-per-page
                                option — fewer rows just leave blank space here
                                instead of shrinking the card, and more rows
                                scroll instead of growing it. */}
                            <div style={{ height: 155, overflowY: "auto" }}>
                              {pagedLogs.length === 0 ? (
                                <div style={{ padding: "10px 6px", color: "var(--od-text-muted)", fontSize: 12 }}>No communication logs available</div>
                              ) : (
                                pagedLogs.map((log, idx) => (
                                  <div
                                    key={log.id ?? idx}
                                    style={{
                                      display: "grid",
                                      gridTemplateColumns: gridCols,
                                      columnGap: 8,
                                      padding: "6px",
                                      borderBottom: "1px solid var(--od-border)",
                                      fontSize: 11.5,
                                    }}
                                  >
                                    {columns.map((col) => (
                                      <div
                                        key={col.key}
                                        style={{
                                          color: col.color,
                                          fontWeight: col.weight,
                                          whiteSpace: "nowrap",
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                        }}
                                      >
                                        {log[col.key] || "NA"}
                                      </div>
                                    ))}
                                  </div>
                                ))
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Pagination — page-size selector, prev/next, total count */}
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8, marginTop: 10, paddingTop: 8, borderTop: "1px solid var(--od-border)" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            <span style={{ fontSize: 10.5, color: "var(--od-text-muted)", whiteSpace: "nowrap" }}>Rows per page:</span>
                            <select
                              value={commLogPageSize}
                              onChange={(e) => { setCommLogPageSize(Number(e.target.value)); resetToFirstPage(); }}
                              style={{ ...selectStyle, padding: "3px 6px" }}
                            >
                              {[5, 10, 25, 50].map((size) => (
                                <option key={size} value={size}>{size}</option>
                              ))}
                            </select>
                          </div>

                          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            <span style={{ fontSize: 10.5, color: "var(--od-text-muted)", whiteSpace: "nowrap" }}>
                              Page {currentLogPage} of {totalLogPages} · {totalLogCount} total
                            </span>
                            <div style={{ display: "flex", gap: 4 }}>
                              <button
                                onClick={() => setCommLogPage((p) => Math.max(1, p - 1))}
                                disabled={currentLogPage <= 1}
                                style={{
                                  fontSize: 11, fontWeight: 600, padding: "4px 10px", borderRadius: 6,
                                  border: "1px solid var(--od-border)", background: "var(--od-surface-alt)",
                                  color: "var(--od-text-secondary)",
                                  cursor: currentLogPage <= 1 ? "not-allowed" : "pointer",
                                  opacity: currentLogPage <= 1 ? 0.5 : 1,
                                }}
                              >
                                Previous
                              </button>
                              <button
                                onClick={() => setCommLogPage((p) => Math.min(totalLogPages, p + 1))}
                                disabled={currentLogPage >= totalLogPages}
                                style={{
                                  fontSize: 11, fontWeight: 600, padding: "4px 10px", borderRadius: 6,
                                  border: "1px solid var(--od-border)", background: "var(--od-surface-alt)",
                                  color: "var(--od-text-secondary)",
                                  cursor: currentLogPage >= totalLogPages ? "not-allowed" : "pointer",
                                  opacity: currentLogPage >= totalLogPages ? 0.5 : 1,
                                }}
                              >
                                Next
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>
            </div>
          </div>

          {/* ══════════════════════════════════════════════
               SECTION 2 — Active Incident / Grid Charts
          ══════════════════════════════════════════════ */}
          <div
            style={{
              background: "var(--od-card-bg)",
              border: "1px solid var(--od-border)",
              borderRadius: 12,
              boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
              marginTop: 10,
              overflow: "hidden",
            }}
          >
            {/* Header */}
            <div
              onClick={() => setIsGridExpanded((prev) => !prev)}
              style={{
                padding: "10px 14px",
                display: "flex",
                alignItems: "center",
                gap: 8,
                background: "var(--od-surface-alt)",
                borderBottom: isGridExpanded ? "1px solid var(--od-border)" : "1px solid transparent",
                borderRadius: "12px 12px 0 0",
                transition: "border-color 0.35s ease",
                minHeight: 52,
                cursor: "pointer",
                userSelect: "none",
              }}
            >
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 8,
                  flexShrink: 0,
                  background: "rgba(59, 130, 246, 0.5)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 16,
                  // boxShadow: "0 2px 8px rgba(37,99,235,0.3)",
                }}
              >
                📊
              </div>

              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: "var(--od-text-primary)", letterSpacing: "0.04em" }}>
                  Active Incident Dashboard
                </div>
                {/* <div style={{ fontSize: 11, color: "var(--od-text-muted)" }}>
                  {isGridExpanded ? "Click to collapse" : "Click to expand"}
                </div> */}
              </div>

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setIsGridExpanded((prev) => !prev);
                }}
                title={isGridExpanded ? "Collapse dashboard" : "Expand dashboard"}
                style={{
                  width: 30,
                  height: 30,
                  borderRadius: 7,
                  flexShrink: 0,
                  background: isGridExpanded ? "rgba(37,99,235,0.08)" : "rgba(59,130,246,0.08)",
                  border: `1px solid ${isGridExpanded ? "rgba(37,99,235,0.22)" : "rgba(59,130,246,0.22)"}`,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "background 0.2s, border-color 0.2s",
                  padding: 0,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "rgba(37,99,235,0.15)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = isGridExpanded ? "rgba(37,99,235,0.08)" : "rgba(59,130,246,0.08)";
                }}
              >
                <svg
                  className={`ai-chevron-icon ${isGridExpanded ? "open" : "closed"}`}
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  fill="none"
                >
                  <path
                    d="M2 4.5L6 8.5L10 4.5"
                    stroke="#2563eb"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>

            {/* Collapsible Body */}
            <div style={makeCollapsibleStyle(isGridExpanded)}>
              <div style={{ padding: "6px 0 0 0" }}>
                <div
                  className="grid-stack grid-stack-instance-1 mt-1"
                  style={{
                    // Only constrain + scroll inside fullscreen; in normal view
                    // let the section grow with the page so there's no nested
                    // scrollbar beside the browser one.
                    overflowY: isFullscreen ? "auto" : "visible",
                    maxHeight: isFullscreen ? "calc(100vh - 120px)" : "initial",
                  }}
                >
                  {layoutToDisplay.map((gridItem) => (
                    <div
                      className="grid-stack-item"
                      gs-x={gridItem.x}
                      gs-y={gridItem.y}
                      gs-w={gridItem.width}
                      gs-h={gridItem.height}
                      key={gridItem.id}
                    >
                      <div
                        className={`grid-stack-item-content ${gridItem?.haveNoBg ? " " : " grid-item"
                          } ${gridItem?.haveOverflowVisible
                            ? " apply__overflow--visible"
                            : ""
                          }`}
                      >
                        {gridItem.component}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
          {/* ── End Sections ── */}
        </div>

        {/* CSS Animations for chevron icons */}
        <style>{`
          .ds-chevron-icon,
          .ai-chevron-icon {
            transition: transform 0.35s cubic-bezier(0.4,0,0.2,1);
          }
          .ds-chevron-icon.open,
          .ai-chevron-icon.open {
            transform: rotate(180deg);
          }
          .ds-chevron-icon.closed,
          .ai-chevron-icon.closed {
            transform: rotate(0deg);
          }
          @keyframes ds-refresh-spin { to { transform: rotate(360deg); } }
          .ds-refresh-spin { animation: ds-refresh-spin 0.8s linear infinite; }
          /* display:flex + align-items:center on the input itself (inline
             style) centers the internal dd-mm-yyyy field vertically; this
             just tidies up the picker icon's own default padding/spacing
             so it centers cleanly alongside it. */
          .ds-date-input::-webkit-calendar-picker-indicator {
            width: 14px;
            height: 14px;
            padding: 0;
            margin: 0 0 0 6px;
            opacity: 0.75;
            cursor: pointer;
          }
          .ds-date-input::-webkit-calendar-picker-indicator:hover {
            opacity: 1;
          }
          .irp-info-wrap {
            position: relative;
            display: inline-flex;
            align-items: center;
          }
          .irp-info-tip {
            position: absolute;
            bottom: calc(100% + 6px);
            left: 50%;
            transform: translateX(-50%);
            background: #111827;
            color: #fff;
            font-size: 11px;
            font-weight: 500;
            line-height: 1.4;
            padding: 6px 9px;
            border-radius: 6px;
            width: 220px;
            white-space: normal;
            box-shadow: 0 4px 12px rgba(0,0,0,0.18);
            opacity: 0;
            visibility: hidden;
            transition: opacity 0.15s ease;
            z-index: 20;
            pointer-events: none;
          }
          .irp-info-tip::after {
            content: "";
            position: absolute;
            top: 100%;
            left: 50%;
            transform: translateX(-50%);
            border: 5px solid transparent;
            border-top-color: #111827; /* matches .irp-info-tip's fixed dark bubble background, not the page text color */
          }
          .irp-info-wrap:hover .irp-info-tip {
            opacity: 1;
            visibility: visible;
          }
        `}</style>
      </div>
    </FullScreen>
  );
};

export default MainDashboard;